import type {
  CreateSpecType,
  MaterialTypeSpec,
} from "@/api/materialTypeService";
import CloseButton from "@/components/buttons/CloseButton";
import { useCreateSpec, useUpdateSpec } from "@/hooks/materialTypeHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { InformationModal, DefaultPrimaryButton } from "@/styles/globalstyles";
import { getAxiosErrorMessage } from "@/utils/healper";
import {
  Modal,
  Box,
  Typography,
  Divider,
  TextField,
  Switch,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/useToast";

interface SaveSpecModalProps {
  open: boolean;
  handleClose: () => void;
  selectedSpec: MaterialTypeSpec;
}

const SaveSpecModal = ({
  open,
  handleClose,
  selectedSpec,
}: SaveSpecModalProps) => {
  const { showToast } = useToast();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<CreateSpecType>({
    spec_name: "",
    description: "",
    is_active: true,
    material_type_id: 0,
  });

  useEffect(() => {
    setFormData({
      spec_name: selectedSpec?.spec_name || "",
      description: selectedSpec?.description || "",
      is_active: selectedSpec?.is_active ?? true,
      material_type_id: selectedSpec?.material_type_id || 0,
    });
  }, [selectedSpec]);

  const createMutation = useCreateSpec();
  const updateMutation = useUpdateSpec();

  useQueryHandler({
    isFetching: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error,
    errorMessage: getAxiosErrorMessage(
      createMutation.error || updateMutation.error
    ),
  });
  
    const validateForm = () => {
      const newErrors: { [key: string]: string } = {};
  
      if (!formData.spec_name) {
        newErrors.spec_name = "กรุณากรอกชื่อ Spec";
      }
  
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };
  
    const handleAdd = () => {
      if (!validateForm()) return;
      createMutation.mutate(formData, {
        onSuccess: () => {
          showToast("บันทึก Spec สำเร็จ", "success");
          onClose();
        }
      });
    };
  
    const handleEdit = () => {
      if (!validateForm()) return;
      if (selectedSpec.id) {
        updateMutation.mutate({ id: selectedSpec.id, params: formData }, {
          onSuccess: () => {
            showToast("บันทึก Spec สำเร็จ", "success");
            onClose();
          }
        });
      }
    };

  const onClose = () => {
    setFormData({
      spec_name: "",
      description: "",
      is_active: true,
      material_type_id: 0,
    });
    setErrors({});
    handleClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={{ ...InformationModal, maxHeight: "90vh" }}>
        <CloseButton onClick={onClose} />
        <Typography variant="h2">
          {selectedSpec.id
            ? "แก้ไข Spec ของ " + selectedSpec.material_type_name
            : "เพิ่ม Spec ของ " + selectedSpec.material_type_name}
        </Typography>
        <Box p={1}>
          <Divider sx={{ my: 1 }} />
          <Box
            mt={1}
            display="flex"
            flexDirection="column"
            gap={1.5}
          >
            <TextField
              fullWidth
              label={
                <>
                  ชื่อ Spec{" "}
                  <Typography component="span" color="error.main">
                    *
                  </Typography>
                </>
              }
              variant="outlined"
              sx={{ mt: 2 }}
              value={formData.spec_name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  spec_name: e.target.value,
                });
                if (errors.material_type_name) {
                  setErrors({ ...errors, material_type_name: "" });
                }
              }}
              error={!!errors.spec_name}
              helperText={errors.spec_name}
            />
            <TextField
              fullWidth
              label="คำอธิบาย"
              variant="outlined"
              sx={{ mt: 2 }}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            {(selectedSpec.id) && (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <Switch
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                <Typography fontWeight="bold">Active</Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="contained"
            sx={DefaultPrimaryButton}
            onClick={selectedSpec.id ? handleEdit : handleAdd}
          >
            บันทึก
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SaveSpecModal;
